import React, { useEffect, useRef, forwardRef, useImperativeHandle, Ref } from 'react';
import { FormikContextType } from 'formik';
import {
  SendCommunicationFirstStepForm,
  SendCommunicationSecondStepForm,
  MessageMask,
  CompensationSentence,
} from 'types';
import { useIntl } from 'common/config/i18n';
import ImpactedStation from './station';
import CardSendCommunication from 'components/molecules/card-send-communication';
import { isEspagne } from 'common/config/env';
import { Checkbox, Col, Row } from 'antd';
import styled from 'styled-components';
import { path } from 'ramda';
import { checkIfAllStationsConcerned, getHasCompensation, sortImpactedStations } from 'common/utils/send-communication';

export type Props = {
  formik: FormikContextType<SendCommunicationSecondStepForm>;
  hasInformations: boolean | undefined;
  stationsConcerned: {
    shortCode: string;
    uicStation: string;
  }[];
  compensationSentencesData: CompensationSentence[];
  formValuesFirstStep: SendCommunicationFirstStepForm;
  selectedMessageMask?: MessageMask;
  defaultPrincipalZoneMessages?: {
    principalZoneLongMessageSaved?: string;
    principalZoneShortMessageSaved?: string;
  };
  onFocusTextField: (contentIndex: number, isPrimary: boolean, isSecondary: boolean, isTertiary: boolean) => void;
  focusedTextFieldIndex?: number;
  primaryEditorWrapperRef?: Ref<HTMLDivElement>;
  secondaryEditorWrapperRef?: Ref<HTMLDivElement>;
  tertiaryEditorWrapperRef?: Ref<HTMLDivElement>;
};

const SubTitle = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: ${path(['theme', 'font', 'weight', 'medium'])};
`;

const ImpactedStations = forwardRef(
  (
    {
      formValuesFirstStep,
      hasInformations,
      stationsConcerned,
      compensationSentencesData,
      selectedMessageMask,
      formik,
      defaultPrincipalZoneMessages,
      onFocusTextField,
      focusedTextFieldIndex,
      primaryEditorWrapperRef,
      secondaryEditorWrapperRef,
      tertiaryEditorWrapperRef,
    }: Props,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ref: any
  ) => {
    const { t } = useIntl();
    const isAllStationsConcerned = checkIfAllStationsConcerned(formValuesFirstStep);

    useEffect(() => {
      const defaultCommunicationChannels = [];
      if (isAllStationsConcerned) {
        defaultCommunicationChannels.push({
          stationShortCode: 'isAllStationsConcerned',
          uicStation: 'isAllStationsConcerned',
          delay: '',
          newSchedule: '',
          stationReplacement: '',
          dock: '',
          principalZoneLongMessage: '',
          principalZoneShortMessage: '',
          secondaryZoneLongMessage: '',
          secondaryZoneShortMessage: '',
          compensationLongMessage: '',
          compensationId: undefined,
          compensationSelected: false,
        });
      } else {
        const impactedStationsSorted = sortImpactedStations(formValuesFirstStep.impactedStations, stationsConcerned);
        impactedStationsSorted.forEach((uicStation) => {
          const currStattion = stationsConcerned?.find((station) => station.uicStation === uicStation);
          defaultCommunicationChannels.push({
            stationShortCode: currStattion?.shortCode,
            uicStation: currStattion?.uicStation,
            delay: '',
            newSchedule: '',
            stationReplacement: '',
            dock: '',
            principalZoneLongMessage: '',
            principalZoneShortMessage: '',
            secondaryZoneLongMessage: '',
            secondaryZoneShortMessage: '',
            compensationLongMessage: '',
            compensationId: undefined,
            compensationSelected: false,
          });
        });
      }
      formik.setFieldValue('communicationContent', defaultCommunicationChannels);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAllStationsConcerned, formValuesFirstStep.impactedStations, selectedMessageMask, stationsConcerned]);

    const handleSpreadTextToAllStations = (
      principalLongMessage?: string,
      principalShortMessage?: string,
      secondaryLongMessage?: string,
      secondaryShortMessage?: string
    ): void => {
      formik.values.communicationContent?.map((content, index) => {
        formik.setFieldValue(`communicationContent[${index}].principalZoneLongMessage`, principalLongMessage);
        formik.setFieldValue(`communicationContent[${index}].principalZoneShortMessage`, principalShortMessage);
        formik.setFieldValue(`communicationContent[${index}].secondaryZoneLongMessage`, secondaryLongMessage);
        formik.setFieldValue(`communicationContent[${index}].secondaryZoneShortMessage`, secondaryShortMessage);
      });
    };

    const handleSpreadDelayToAllDelays = (delay?: string): void => {
      formik.values.communicationContent?.map((content, index) => {
        formik.setFieldValue(`communicationContent[${index}].delay`, delay);
      });
    };

    const hasMultipleStations = formik?.values?.communicationContent && formik.values.communicationContent.length > 1;
    const hasCompensationZone = getHasCompensation(formValuesFirstStep.customersSelection, selectedMessageMask);

    const impactedStationRef = useRef<HTMLFormElement>(null);
    useImperativeHandle(ref, () => ({
      insertText(text: string, isPrimary: boolean, isSecondary: boolean, isTertiary: boolean) {
        impactedStationRef?.current?.insertText(text, isPrimary, isSecondary, isTertiary);
      },
    }));

    return (
      <>
        {(hasInformations || hasCompensationZone) &&
          formik.values.communicationContent?.map((content, index) => (
            <CardSendCommunication
              title={
                isAllStationsConcerned
                  ? t('form', 'ALL_STATIONS_CONCERNED')
                  : `${t('form', 'SELECTED_STATION_H3')}${content.stationShortCode}`
              }
              key={`${index}-${content.uicStation}`}
              style={{ marginBottom: '1rem' }}
              wrraperStyle={{ paddingTop: '5px' }}
            >
              <ImpactedStation
                compensationSentencesData={compensationSentencesData}
                onFocusTextField={onFocusTextField}
                key={index}
                selectedMessageMask={selectedMessageMask}
                formik={formik}
                content={content}
                contentIndex={index}
                defaultPrincipalZoneMessages={defaultPrincipalZoneMessages}
                customersSelection={formValuesFirstStep.customersSelection}
                hasMultipleStations={hasMultipleStations}
                handleSpreadTextToAllStations={handleSpreadTextToAllStations}
                handleSpreadDelayToAllDelays={handleSpreadDelayToAllDelays}
                ref={index === focusedTextFieldIndex ? impactedStationRef : undefined}
                primaryEditorWrapperRef={index === focusedTextFieldIndex ? primaryEditorWrapperRef : undefined}
                secondaryEditorWrapperRef={index === focusedTextFieldIndex ? secondaryEditorWrapperRef : undefined}
                tertiaryEditorWrapperRef={index === focusedTextFieldIndex ? tertiaryEditorWrapperRef : undefined}
              />
            </CardSendCommunication>
          ))}
        {isEspagne && hasCompensationZone && (
          <CardSendCommunication>
            <Row gutter={16}>
              <Col span={14}></Col>
              <Col span={10}>
                <Row justify="space-between" align="middle">
                  <SubTitle>{t('sendCommunication', 'compensation', 'confirmCompensationLabel')}</SubTitle>
                  <Checkbox
                    checked={formik.values.compensationSelected}
                    onChange={(event) => {
                      formik.setFieldValue(`compensationSelected`, event.target.checked);
                      // MDW needs to know the compensationSelected value for each station
                      formik.values.communicationContent?.forEach((_content, index) => {
                        formik.setFieldValue(
                          `communicationContent[${index}].compensationSelected`,
                          event.target.checked
                        );
                      });
                    }}
                  />
                </Row>
              </Col>
            </Row>
          </CardSendCommunication>
        )}
      </>
    );
  }
);

ImpactedStations.displayName = 'ImpactedStations';

export default ImpactedStations;
